// ============================================================
// REAL-TIME EVENT ENGINE (FRONTEND)
// Purpose:
// - Handle websocket events cleanly
// - Merge updates into state (no reloads)
// - Prevent duplicates / flicker
// - Maintain UI stability under load
// ============================================================

import { useEffect, useReducer, useState, useCallback, useRef } from "react";

// ============================================================
// 🔷 EVENT TYPES (LOCK THESE)
// ============================================================

export const EVENT_TYPES = {
  JOB_CREATED: "job_created",
  JOB_UPDATED: "job_updated",
  CONTRACTOR_ASSIGNED: "contractor_assigned",
  CONTRACTOR_ACTION: "contractor_action",
  SLA_BREACH: "sla_breach",
};


// ============================================================
// 🔷 NORMALISE INCOMING EVENT
// Ensures consistent structure
// ============================================================

function normaliseEvent(event) {
  const data = event.data || {};
  return {
    id: event.job_id || data.job_id || event.id,
    job_reference: data.job_reference || event.job_reference,
    park_name: data.site_name || data.park_name || event.park_name,
    site_name: data.site_name || event.site_name,
    plot: data.plot || event.plot,
    status: data.status || data.status_change?.to || event.status,
    stage: data.stage || event.stage,
    next_action: data.next_action || event.next_action,
    contractor_name: data.contractor_name || event.contractor_name,
    contractor_id: data.contractor_id || event.contractor_id,
    priority: data.priority || event.priority,
    description: data.description || event.description,
    created_at: data.created_at || event.created_at,
    timestamp: event.timestamp,
    type: event.type,
    raw: event,
  };
}


// ============================================================
// 🔷 SAFE MERGE FUNCTION
// Core logic — no duplicates, no flicker
// ============================================================

export function mergeJobs(existingJobs, incomingEvent) {
  const event = normaliseEvent(incomingEvent);

  if (!event.id) return existingJobs;

  const index = existingJobs.findIndex(j => 
    j.id === event.id || 
    j.internal_id === event.id ||
    j.job_reference === event.job_reference
  );

  // ---------------------------------------
  // NEW JOB
  // ---------------------------------------
  if (incomingEvent.type === EVENT_TYPES.JOB_CREATED) {
    // prevent duplicate insert
    if (index !== -1) return existingJobs;

    return [
      {
        ...event,
        id: event.id,
        internal_id: event.id,
        isNew: true,
        lastUpdated: Date.now(),
      },
      ...existingJobs
    ];
  }

  // ---------------------------------------
  // UPDATE EXISTING
  // ---------------------------------------
  if (index !== -1) {
    const updated = [...existingJobs];
    const existing = updated[index];

    updated[index] = {
      ...existing,
      // Only update fields that are present in the event
      ...(event.status && { status: event.status }),
      ...(event.contractor_name && { 
        contractor_name: event.contractor_name,
        assigned_contractor_name: event.contractor_name 
      }),
      ...(event.contractor_id && { 
        contractor_id: event.contractor_id,
        assigned_contractor_id: event.contractor_id 
      }),
      ...(event.priority && { priority: event.priority }),
      ...(event.next_action && { next_action: event.next_action }),
      // SLA breach flag
      ...(incomingEvent.type === EVENT_TYPES.SLA_BREACH && { 
        sla_breached: true,
        sla_breach_type: event.raw?.data?.breach_type 
      }),
      isUpdated: true,
      lastUpdated: Date.now(),
    };

    return updated;
  }

  // ---------------------------------------
  // EDGE CASE: update arrives before create
  // ---------------------------------------
  return [
    {
      ...event,
      id: event.id,
      internal_id: event.id,
      isRecovered: true,
      lastUpdated: Date.now(),
    },
    ...existingJobs
  ];
}


// ============================================================
// 🔷 REDUCER (FOR STATE MANAGEMENT)
// Use with useReducer or Zustand
// ============================================================

export function jobsReducer(state, action) {
  switch (action.type) {

    case "INIT":
      return action.payload.map(j => ({
        ...j,
        lastUpdated: Date.now(),
      }));

    case "REALTIME_EVENT":
      return mergeJobs(state, action.payload);

    case "CLEAR_FLAGS":
      return state.map(j => ({
        ...j,
        isNew: false,
        isUpdated: false,
        isRecovered: false,
      }));

    case "REFRESH":
      // Full refresh from API - merge to preserve flags
      return action.payload.map(newJob => {
        const existing = state.find(j => 
          j.id === newJob.id || 
          j.internal_id === newJob.internal_id
        );
        return {
          ...newJob,
          isNew: existing?.isNew || false,
          isUpdated: existing?.isUpdated || false,
          lastUpdated: existing?.lastUpdated || Date.now(),
        };
      });

    default:
      return state;
  }
}


// ============================================================
// 🔷 WEBSOCKET CONNECTION CONFIG
// ============================================================

const RECONNECT_DELAY_BASE = 2000; // Start at 2 seconds
const MAX_RECONNECT_ATTEMPTS = 5;
const PING_INTERVAL = 30000;
const FLAG_CLEAR_DELAY = 3000;

/**
 * Calculate exponential backoff delay
 * Attempt 0: 2s, 1: 4s, 2: 8s, 3: 16s, 4: 32s (max)
 */
function getReconnectDelay(attempt) {
  const delay = RECONNECT_DELAY_BASE * Math.pow(2, attempt);
  return Math.min(delay, 32000); // Cap at 32 seconds
}

/**
 * Build WebSocket URL from HTTP URL
 */
function getWebSocketUrl(channel, userId = 'anonymous') {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
  const host = backendUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${host}/api/realtime/ws/${channel}?user_id=${userId}`;
}


// ============================================================
// 🔷 REAL-TIME HOOK (ENHANCED)
// Handles events + merge with useReducer
// ============================================================

export function useRealtimeJobs(channel = 'wyldecrest_helpdesk', initialJobs = [], options = {}) {
  const { userId = 'anonymous', enabled = true } = options;
  
  const [jobs, dispatch] = useReducer(jobsReducer, initialJobs);
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const flagClearTimeoutRef = useRef(null);

  // Initialize jobs when initialJobs changes
  useEffect(() => {
    if (initialJobs.length > 0) {
      dispatch({ type: "INIT", payload: initialJobs });
    }
  }, [initialJobs]);

  // Auto-clear "new/updated" flags after short time
  useEffect(() => {
    if (flagClearTimeoutRef.current) {
      clearTimeout(flagClearTimeoutRef.current);
    }
    
    const hasFlags = jobs.some(j => j.isNew || j.isUpdated || j.isRecovered);
    if (hasFlags) {
      flagClearTimeoutRef.current = setTimeout(() => {
        dispatch({ type: "CLEAR_FLAGS" });
      }, FLAG_CLEAR_DELAY);
    }

    return () => {
      if (flagClearTimeoutRef.current) {
        clearTimeout(flagClearTimeoutRef.current);
      }
    };
  }, [jobs]);

  const connect = useCallback(() => {
    if (!enabled || wsRef.current?.readyState === WebSocket.OPEN) return;

    const url = getWebSocketUrl(channel, userId);
    
    try {
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, PING_INTERVAL);
      };

      wsRef.current.onmessage = (msg) => {
        try {
          // Handle pong
          if (msg.data === 'pong') return;

          const event = JSON.parse(msg.data);
          
          // Skip connection/status messages
          if (['connected', 'pong', 'status'].includes(event.type)) return;

          setLastEvent(event);

          // Add to event log (keep last 50)
          setEvents(prev => [event, ...prev].slice(0, 50));

          // Merge into state
          dispatch({
            type: "REALTIME_EVENT",
            payload: event,
          });

        } catch (err) {
          // Silent parse errors - don't spam console
        }
      };

      wsRef.current.onclose = () => {
        setConnected(false);
        clearInterval(pingIntervalRef.current);

        // Attempt reconnect with exponential backoff
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = getReconnectDelay(reconnectAttempts.current);
          reconnectAttempts.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
        // After max attempts, stop trying silently
      };

      wsRef.current.onerror = () => {
        // Silent error handling - WebSocket errors are expected in some environments
      };

    } catch (err) {
      // Silent connection failure - WebSocket may not be available
    }
  }, [channel, userId, enabled]);

  const disconnect = useCallback(() => {
    clearInterval(pingIntervalRef.current);
    clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const refreshJobs = useCallback((newJobs) => {
    dispatch({ type: "REFRESH", payload: newJobs });
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }
    return () => disconnect();
  }, [connect, disconnect, enabled]);

  return {
    jobs,
    connected,
    events,
    lastEvent,
    reconnect: connect,
    disconnect,
    refreshJobs,
    dispatch,
  };
}


// ============================================================
// 🔷 SIMPLE REALTIME HOOK (Original API compatible)
// For views that don't need full reducer pattern
// ============================================================

export function useRealtime(channel, options = {}) {
  const {
    onJobCreated,
    onJobUpdated,
    onContractorAssigned,
    onSlaBreach,
    userId = 'anonymous',
    enabled = true
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (!enabled || wsRef.current?.readyState === WebSocket.OPEN) return;

    const url = getWebSocketUrl(channel, userId);
    
    try {
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;

        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, PING_INTERVAL);
      };

      wsRef.current.onmessage = (event) => {
        try {
          if (event.data === 'pong') return;

          const data = JSON.parse(event.data);
          setLastEvent(data);

          switch (data.type) {
            case 'connected':
              break;
            case EVENT_TYPES.JOB_CREATED:
              onJobCreated?.(data);
              break;
            case EVENT_TYPES.JOB_UPDATED:
              onJobUpdated?.(data);
              break;
            case EVENT_TYPES.CONTRACTOR_ASSIGNED:
              onContractorAssigned?.(data);
              break;
            case EVENT_TYPES.SLA_BREACH:
              onSlaBreach?.(data);
              break;
            default:
              break;
          }
        } catch (err) {
          // Silent parse errors
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);

        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = getReconnectDelay(reconnectAttempts.current);
          reconnectAttempts.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      wsRef.current.onerror = () => {
        // Silent error handling
      };

    } catch (err) {
      // Silent connection failure
    }
  }, [channel, userId, enabled, onJobCreated, onJobUpdated, onContractorAssigned, onSlaBreach]);

  const disconnect = useCallback(() => {
    clearInterval(pingIntervalRef.current);
    clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }
    return () => disconnect();
  }, [connect, disconnect, enabled]);

  return {
    isConnected,
    lastEvent,
    reconnect: connect,
    disconnect,
  };
}


// ============================================================
// 🔷 UI HELPERS (VISUAL FEEDBACK)
// ============================================================

export function getRowHighlight(job) {
  if (job.isNew) return "ring-2 ring-blue-300 animate-pulse";
  if (job.isUpdated) return "bg-yellow-50 transition-colors duration-500";
  if (job.isRecovered) return "bg-green-50 transition-colors duration-500";
  return "";
}

export function getCardHighlight(job) {
  if (job.isNew) return "ring-2 ring-blue-400 shadow-lg shadow-blue-100";
  if (job.isUpdated) return "ring-1 ring-amber-300 bg-amber-50/30";
  if (job.isRecovered) return "ring-1 ring-green-300 bg-green-50/30";
  return "";
}


// ============================================================
// 🔷 EVENT TO TOAST (FOR NOTIFICATIONS)
// ============================================================

export function eventToToast(event) {
  if (!event?.type) return null;

  const data = event.data || {};

  switch (event.type) {
    case EVENT_TYPES.JOB_CREATED:
      return {
        type: 'info',
        title: 'New Job',
        message: `${data.job_reference || 'Job'} created at ${data.site_name || 'Unknown'}`,
        duration: 4000
      };

    case EVENT_TYPES.JOB_UPDATED:
      if (data.status_change) {
        return {
          type: 'info',
          title: 'Job Updated',
          message: `Status changed to ${data.status_change.to}`,
          duration: 3000
        };
      }
      return null;

    case EVENT_TYPES.CONTRACTOR_ASSIGNED:
      return {
        type: 'success',
        title: 'Contractor Assigned',
        message: `${data.contractor_name || 'Contractor'} assigned`,
        duration: 3000
      };

    case EVENT_TYPES.SLA_BREACH:
      return {
        type: 'warning',
        title: 'SLA Breach',
        message: `${data.job_reference || 'Job'} - ${data.breach_type || 'SLA'} overdue`,
        duration: 6000
      };

    default:
      return null;
  }
}


// ============================================================
// 🔷 EXPORTS
// ============================================================

export default {
  useRealtimeJobs,
  useRealtime,
  mergeJobs,
  jobsReducer,
  eventToToast,
  getRowHighlight,
  getCardHighlight,
  EVENT_TYPES,
};
